/**
 * Archivos clínicos: combina Cloudinary (almacena el binario) con Firestore
 * (almacena la metadata del archivo). Soft-delete: marcamos deleted=true en
 * Firestore y dejamos el binario en Cloudinary (no se puede borrar sin API key
 * privada).
 */

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  serverTimestamp,
  setDoc,
  updateDoc,
  getDocs,
  Timestamp,
  type DocumentData,
  type FirestoreDataConverter,
  type WithFieldValue,
} from 'firebase/firestore';

import { firestore } from './config';
import { subirACloudinary } from '@/services/cloudinary';
import { USE_API, api } from '@/services/api/client';

export interface ArchivoCaso {
  id: string;
  doctorUid: string;
  doctorNombre: string | null;
  name: string;
  fullPath: string; // alias del id, mantenido para compatibilidad con el screen
  url: string;
  publicId: string;
  mimeType: string | null;
  size: number;
  subidoEn: Timestamp;
  deleted: boolean;
}

const conv: FirestoreDataConverter<ArchivoCaso> = {
  toFirestore(data: WithFieldValue<ArchivoCaso>): DocumentData {
    return data as DocumentData;
  },
  fromFirestore(snap, options) {
    const data = snap.data(options) as Omit<ArchivoCaso, 'id' | 'fullPath'>;
    return { id: snap.id, fullPath: snap.id, ...data };
  },
};

export const archivosCollection = collection(firestore, 'archivos_clinicos').withConverter(conv);

export async function listarArchivosDoctor(doctorUid: string): Promise<ArchivoCaso[]> {
  if (USE_API) {
    const rows = await api.get(`/archivos_clinicos?doctorUid=${encodeURIComponent(doctorUid)}&deleted=false&_order=-subido_en`);
    return (rows ?? []).map((r: any) => ({ ...r, fullPath: r.id }));
  }
  const q = query(
    archivosCollection,
    where('doctorUid', '==', doctorUid),
    where('deleted', '==', false),
    orderBy('subidoEn', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data());
}

export interface SubirArchivoInput {
  doctorUid: string;
  doctorNombre?: string;
  file: Blob | File;
  filename: string;
  onProgress?: (pct: number) => void;
}

export async function subirArchivoCaso({
  doctorUid,
  doctorNombre,
  file,
  filename,
  onProgress,
}: SubirArchivoInput): Promise<string> {
  // 1. Subir a Cloudinary
  const folder = `clinicat/casos/${doctorUid}`;
  const result = await subirACloudinary(file, { folder, filename, onProgress });
  const mimeType = (file as File).type ?? null;
  const size = (file as File).size ?? result.bytes;

  // 2. Guardar metadata
  if (USE_API) {
    await api.post('/archivos_clinicos', {
      doctorUid,
      doctorNombre: doctorNombre ?? null,
      name: filename,
      url: result.url,
      publicId: result.publicId,
      mimeType,
      size,
      deleted: false,
    });
    return result.url;
  }
  const docRef = doc(archivosCollection);
  await setDoc(docRef, {
    doctorUid,
    doctorNombre: doctorNombre ?? null,
    name: filename,
    url: result.url,
    publicId: result.publicId,
    mimeType,
    size,
    subidoEn: serverTimestamp() as any,
    deleted: false,
  } as any);
  return result.url;
}

/** Soft-delete: marca el doc como deleted=true. El binario queda en Cloudinary. */
export async function eliminarArchivoCaso(docId: string) {
  if (USE_API) return void (await api.patch(`/archivos_clinicos/${docId}`, { deleted: true }));
  await updateDoc(doc(archivosCollection, docId), {
    deleted: true,
    eliminadoEn: serverTimestamp(),
  } as any);
}
